import { Helmet } from "react-helmet-async";

const SITE_URL = "https://minfesttale.dk";
const DEFAULT_OG_IMAGE =
  "https://static.prod-images.emergentagent.com/jobs/8c033565-2945-4a08-9475-67d051518b07/images/88f07a9d44351f45c87e558d4d0741ae6eebb550327ed7a5dde16e2454fc12eb.png";

/**
 * SEO component — sets unique title, meta description, canonical URL,
 * Open Graph + Twitter Card tags, and optional JSON-LD schema per page.
 *
 * Usage:
 *   <SEO
 *     title="..."
 *     description="..."
 *     path="/blog/abc"
 *     image="/og-blog-abc.png"  // optional
 *     schema={{ "@context": "https://schema.org", "@type": "Article", ... }}
 *   />
 */
export default function SEO({
  title,
  description,
  path = "/",
  image = DEFAULT_OG_IMAGE,
  schema = null,
  type = "website",
}) {
  const url = `${SITE_URL}${path}`;
  const fullTitle = title?.includes("MinFesttale")
    ? title
    : `${title} | MinFesttale.dk`;
  const fullImage = image.startsWith("http") ? image : `${SITE_URL}${image}`;

  return (
    <Helmet>
      <title>{fullTitle}</title>
      <meta name="description" content={description} />
      <link rel="canonical" href={url} />

      {/* Open Graph */}
      <meta property="og:type" content={type} />
      <meta property="og:url" content={url} />
      <meta property="og:title" content={fullTitle} />
      <meta property="og:description" content={description} />
      <meta property="og:image" content={fullImage} />
      <meta property="og:site_name" content="MinFesttale.dk" />
      <meta property="og:locale" content="da_DK" />

      {/* Twitter Card */}
      <meta name="twitter:card" content="summary_large_image" />
      <meta name="twitter:title" content={fullTitle} />
      <meta name="twitter:description" content={description} />
      <meta name="twitter:image" content={fullImage} />

      {/* JSON-LD structured data (one or more schemas) */}
      {schema && (Array.isArray(schema) ? schema : [schema]).map((s, i) => (
        <script key={i} type="application/ld+json">{JSON.stringify(s)}</script>
      ))}
    </Helmet>
  );
}

/** Pre-built schema generators for convenience */
export const websiteSchema = () => ({
  "@context": "https://schema.org",
  "@type": "WebSite",
  name: "MinFesttale.dk",
  url: SITE_URL,
  inLanguage: "da-DK",
  description:
    "AI-genererede personlige festtaler til bryllup, konfirmation, runde fødselsdage og andre festlige anledninger.",
  potentialAction: {
    "@type": "SearchAction",
    target: `${SITE_URL}/blog?q={search_term_string}`,
    "query-input": "required name=search_term_string",
  },
});

export const organizationSchema = () => ({
  "@context": "https://schema.org",
  "@type": "Organization",
  name: "LearnIQ ApS",
  url: SITE_URL,
  logo: `${SITE_URL}/brand/logo-512.png`,
  email: "hej@minfesttale.dk",
  sameAs: [
    "https://facebook.com/minfesttale",
    "https://instagram.com/minfesttale",
  ],
  address: {
    "@type": "PostalAddress",
    streetAddress: "Amaliegade 6, 2. sal",
    postalCode: "1256",
    addressLocality: "København K",
    addressCountry: "DK",
  },
  vatID: "DK42111007",
});

export const articleSchema = ({ title, description, path, datePublished, dateModified, category, image }) => ({
  "@context": "https://schema.org",
  "@type": "Article",
  headline: title,
  description,
  url: `${SITE_URL}${path}`,
  inLanguage: "da-DK",
  articleSection: category,
  datePublished: datePublished || "2026-02-01T09:00:00+01:00",
  dateModified: dateModified || datePublished || "2026-02-01T09:00:00+01:00",
  image: image
    ? (image.startsWith("http") ? image : `${SITE_URL}${image}`)
    : DEFAULT_OG_IMAGE,
  author: {
    "@type": "Organization",
    name: "MinFesttale.dk",
    url: SITE_URL,
  },
  publisher: {
    "@type": "Organization",
    name: "LearnIQ ApS",
    logo: {
      "@type": "ImageObject",
      url: DEFAULT_OG_IMAGE,
    },
  },
  mainEntityOfPage: {
    "@type": "WebPage",
    "@id": `${SITE_URL}${path}`,
  },
});

export const faqSchema = (faqs) => ({
  "@context": "https://schema.org",
  "@type": "FAQPage",
  mainEntity: faqs.map(({ q, a }) => ({
    "@type": "Question",
    name: q,
    acceptedAnswer: {
      "@type": "Answer",
      text: a,
    },
  })),
});

export const productSchema = ({ name, description, price }) => ({
  "@context": "https://schema.org",
  "@type": "Product",
  name,
  description,
  offers: {
    "@type": "Offer",
    price: String(price),
    priceCurrency: "DKK",
    availability: "https://schema.org/InStock",
    url: `${SITE_URL}/tale`,
  },
});
